/**
 * Thin fetch wrapper. Same-origin in dev (Vite proxies /api to the server) and
 * in production (Express serves the built client), so no base URL is needed.
 */

export class ApiError extends Error {
  constructor(status, message, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details ?? {};
  }
}

/**
 * The brand this tab is working on, for accounts running several. Sent with
 * every request so the server acts on the brand on screen — not whichever one
 * another tab switched to last. Set by BrandProvider; null for everyone else.
 */
let currentBrandId = null;
export function setBrandHeader(id) {
  currentBrandId = id ?? null;
}

async function request(path, { method = 'GET', body, signal, headers } = {}) {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'include', // The auth cookie is httpOnly; it has to ride along.
    signal,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(currentBrandId ? { 'X-Affily-Brand': currentBrandId } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return null;

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    if (!res.ok) throw new ApiError(res.status, text.slice(0, 200) || res.statusText);
    throw new ApiError(res.status, 'The server returned a malformed response');
  }

  if (!res.ok) {
    throw new ApiError(
      res.status,
      data?.error?.message ?? res.statusText ?? 'Request failed',
      data?.error?.details,
    );
  }
  return data;
}

/** Build a query string, dropping empty values so URLs stay clean and cacheable. */
export function qs(params = {}) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (!value.length) continue;
      search.set(key, value.join(','));
    } else {
      search.set(key, String(value));
    }
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}

export const api = {
  get: (path, opts) => request(path, { ...opts, method: 'GET' }),
  post: (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
  patch: (path, body, opts) => request(path, { ...opts, method: 'PATCH', body }),
  put: (path, body, opts) => request(path, { ...opts, method: 'PUT', body }),
  delete: (path, opts) => request(path, { ...opts, method: 'DELETE' }),

  /** Multipart upload — no JSON Content-Type, the browser sets the boundary. */
  async upload(path, formData) {
    const res = await fetch(`/api${path}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      throw new ApiError(res.status, data?.error?.message ?? 'Upload failed', data?.error?.details);
    }
    return data;
  },
};
